import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Image } from 'expo-image';
import {
  buildDeck,
  displayUrlsFor,
  photoPositions,
  type DeckItem,
  type Slide,
  type SlideshowBounds,
} from './deck';
import { resolveExitFullscreenStage, resolveOpenStage, type SlideshowStage } from './stage';

/**
 * All the state the observation presentation runs on: which deck is up,
 * which surface is showing it, where in the deck we are, whether autoplay is
 * ticking, and the prefetch that keeps the next photos warm. The two
 * surfaces (PresentationPanel, PresentationFullscreen) render this and
 * nothing else, so the deck's position survives the panel <-> fullscreen
 * jump instead of resetting.
 *
 * The deck is a SNAPSHOT, not a subscription. Map.web.tsx hands in
 * `buildSnapshot`, which reads the map's live bounds and the posts store;
 * the controller calls it twice only: on `open`, and on `rebuildDeck` when
 * the presenter has panned away and asks for the deck to catch up. Panning
 * on its own never touches the deck.
 */

const AUTOPLAY_MS = 5000;
// Three ahead, one behind. Ahead is what a presenter actually walks into;
// the one behind covers the "wait, go back" that follows a click too many.
const PREFETCH_AHEAD = 3;
const PREFETCH_BEHIND = 1;

/** One deck's worth of slides plus the map bounds they came from. */
export interface SlideshowSnapshot {
  slides: Slide[];
  bounds: SlideshowBounds;
}

export interface SlideshowController {
  stage: SlideshowStage;
  deck: DeckItem[];
  index: number;
  /** The deck item on screen, or undefined for an empty deck. */
  current: DeckItem | undefined;
  /** 1-based photo number of `current`, or null on a title card. */
  photoIndex: number | null;
  photoCount: number;
  /** Map bounds the presented deck was built from, null before an open. */
  presentedBounds: SlideshowBounds | null;
  autoplay: boolean;
  next: () => void;
  prev: () => void;
  goTo: (index: number) => void;
  toggleAutoplay: () => void;
  /** Opens onto the surface this device gets (panel on the desktop shell).
   *  Returns false when there was nothing to present. */
  open: () => boolean;
  /** Rebuilds the deck from where the map is now, without leaving the
   *  surface the presenter is on. Back to the first title card, autoplay
   *  off: it is a new presentation of a new area. */
  rebuildDeck: () => void;
  enterFullscreen: () => void;
  /** Ends the presentation (Sept 9 2026: fullscreen's close, Escape, and a
   *  browser fullscreen exit all end it now; Dock is the only way to the
   *  panel from here). */
  exitFullscreen: () => void;
  /** Reaches the docked panel from fullscreen. Desktop shell only; a no-op
   *  everywhere else, since there is no panel to dock into. */
  dock: () => void;
  /** Ends the presentation outright. */
  close: () => void;
}

/** Display URLs worth having in cache from `index`: the next few photo
 *  items and the one before. Title cards carry no image and are skipped
 *  rather than counted, so opening on a title card still warms the first
 *  photos behind it. */
function prefetchTargets(deck: DeckItem[], index: number): string[] {
  if (deck.length === 0) return [];
  const urls: string[] = [];
  const at = (offset: number) =>
    deck[(((index + offset) % deck.length) + deck.length) % deck.length];

  let ahead = 0;
  for (let step = 1; step <= deck.length && ahead < PREFETCH_AHEAD; step++) {
    const item = at(step);
    if (item.kind === 'title') continue;
    urls.push(...displayUrlsFor(item));
    ahead++;
  }
  let behind = 0;
  for (let step = 1; step <= deck.length && behind < PREFETCH_BEHIND; step++) {
    const item = at(-step);
    if (item.kind === 'title') continue;
    urls.push(...displayUrlsFor(item));
    behind++;
  }
  return urls;
}

export function useSlideshowController({
  buildSnapshot,
  isDesktopShell,
}: {
  /** Reads the map's current bounds and returns the deck they imply.
   *  Returns null while the map is not ready to be asked. */
  buildSnapshot: () => SlideshowSnapshot | null;
  isDesktopShell: boolean;
}): SlideshowController {
  const [stage, setStage] = useState<SlideshowStage>('closed');
  const [snapshot, setSnapshot] = useState<SlideshowSnapshot | null>(null);
  const [index, setIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(false);

  // Held in a ref so open/rebuildDeck stay stable across renders even when
  // the caller hands in a fresh closure.
  const buildRef = useRef(buildSnapshot);
  buildRef.current = buildSnapshot;

  const slides = snapshot?.slides;
  const deck = useMemo(() => buildDeck(slides ?? []), [slides]);
  const { numberByIndex, total } = useMemo(() => photoPositions(deck), [deck]);

  const goTo = useCallback(
    (to: number) => {
      setIndex(deck.length ? ((to % deck.length) + deck.length) % deck.length : 0);
    },
    [deck.length]
  );
  const next = useCallback(() => {
    setIndex((i) => (deck.length ? (i + 1) % deck.length : 0));
  }, [deck.length]);
  const prev = useCallback(() => {
    setIndex((i) => (deck.length ? (i - 1 + deck.length) % deck.length : 0));
  }, [deck.length]);
  const toggleAutoplay = useCallback(() => setAutoplay((a) => !a), []);

  // A fresh deck always starts at the front with autoplay off, whether it
  // came from Present or from Update.
  const takeSnapshot = useCallback(() => {
    const taken = buildRef.current();
    if (!taken) return false;
    setSnapshot(taken);
    setIndex(0);
    setAutoplay(false);
    return true;
  }, []);

  const open = useCallback(() => {
    if (!takeSnapshot()) return false;
    setStage(resolveOpenStage());
    return true;
  }, [takeSnapshot]);
  const rebuildDeck = useCallback(() => {
    takeSnapshot();
  }, [takeSnapshot]);
  const enterFullscreen = useCallback(() => setStage('fullscreen'), []);
  const exitFullscreen = useCallback(() => setStage(resolveExitFullscreenStage()), []);
  const dock = useCallback(() => {
    if (isDesktopShell) setStage('panel');
  }, [isDesktopShell]);
  const close = useCallback(() => setStage('closed'), []);

  // Autoplay: off by default in both surfaces — a live presenter drives it
  // by hand most of the time. A timer per slide rather than one repeating
  // interval, so a manual Next restarts the dwell instead of cutting it
  // short.
  useEffect(() => {
    if (stage === 'closed' || !autoplay || deck.length < 2) return;
    const id = setTimeout(next, AUTOPLAY_MS);
    return () => clearTimeout(id);
  }, [stage, autoplay, index, deck.length, next]);

  // Keep the neighbours warm. Runs on open and on every move, so by the
  // time a presenter clicks Next the file is usually already in cache and
  // the stage swaps without a spinner. A rebuilt deck is a new deck here,
  // so Update warms its first photos exactly as an open does.
  useEffect(() => {
    if (stage === 'closed') return;
    for (const url of prefetchTargets(deck, index)) {
      Image.prefetch(url).catch(() => {});
    }
  }, [stage, index, deck]);

  const current = deck[index];
  return {
    stage,
    deck,
    index,
    current,
    photoIndex: numberByIndex.get(index) ?? null,
    photoCount: total,
    presentedBounds: snapshot?.bounds ?? null,
    autoplay,
    next,
    prev,
    goTo,
    toggleAutoplay,
    open,
    rebuildDeck,
    enterFullscreen,
    exitFullscreen,
    dock,
    close,
  };
}
